<template>
  <div class="mapbox-test-page">
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <h1>Pipeline Fastener Monitoring System</h1>
          <p>This system monitors critical fasteners along the pipeline using torque, strain, temperature, and movement sensors. Hover over zone markers to see fastener health details.</p>
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12">
          <v-card class="map-card">
            <v-card-title>Pipeline Monitor Map</v-card-title>
            <v-card-text class="pa-0">
              <div class="map-container-wrapper">
                <div style="height: 600px; position: relative;">
                  <MapboxPipelineMap
                    ref="pipelineMap"
                    :is-dark-mode="true"
                    @segment-selected="onSegmentSelected"
                    @zone-selected="onZoneSelected"
                    @zone-hover="onZoneHover"
                    @zone-hover-end="onZoneHoverEnd"
                  />
                  
                  <!-- Zone Popup Overlay -->
                  <div 
                    v-if="zonePopup.show" 
                    class="zone-popup-card"
                    :style="zonePopupStyle"
                    @mouseenter="onPopupMouseEnter"
                    @mouseleave="onPopupMouseLeave"
                  >
                    <div class="zone-popup-header">
                      <h3 class="zone-title">{{ zonePopup.zone.name }}</h3>
                      <button 
                        class="close-btn"
                        @click="hideZonePopup"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div class="zone-popup-content">
                      <div class="zone-details-section">
                        <div class="zone-info-row">
                          <span class="zone-label">Zone ID:</span>
                          <span class="zone-value">{{ zonePopup.zone.id }}</span>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Mile Marker:</span>
                          <span class="zone-value">{{ zonePopup.zone.mileMarker }}</span>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Status:</span>
                          <v-chip 
                            x-small 
                            :color="getZoneStatusColor(zonePopup.zone.status)"
                            dark
                          >
                            {{ zonePopup.zone.status.toUpperCase() }}
                          </v-chip>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Alerts:</span>
                          <span class="zone-value alert-count">{{ zonePopup.zone.alerts }}</span>
                        </div>
                      </div>
                      
                      <hr class="zone-divider" />
                      
                      <div class="sensors-section">
                        <h4 class="sensors-header">
                          🔩 Fastener Sensors ({{ zonePopup.zone.sensors.length }})
                        </h4>
                        
                        <div class="sensors-list">
                          <div 
                            v-for="sensor in zonePopup.zone.sensors.slice(0, 6)" 
                            :key="sensor.id"
                            class="sensor-item"
                          >
                            <div class="sensor-info">
                              <div class="sensor-name">{{ sensor.name }}</div>
                              <div class="sensor-details">
                                <v-chip 
                                  x-small 
                                  outlined 
                                  :color="getSensorTypeColor(sensor.type)"
                                  class="sensor-type-chip"
                                >
                                  {{ sensor.type }}
                                </v-chip>
                                <span class="sensor-value">{{ sensor.value }}</span>
                              </div>
                            </div>
                            <v-icon 
                              small 
                              :color="getSensorStatusColor(sensor.status)"
                              class="sensor-status-icon"
                            >
                              {{ sensor.status === 'healthy' ? 'mdi-check-circle' : 
                                 sensor.status === 'warning' ? 'mdi-alert-circle' : 'mdi-close-circle' }}
                            </v-icon>
                          </div>
                          
                          <div 
                            v-if="zonePopup.zone.sensors.length > 6" 
                            class="more-sensors"
                          >
                            +{{ zonePopup.zone.sensors.length - 6 }} more sensors
                          </div>
                        </div>
                      </div>
                      
                      <hr class="zone-divider" />
                      
                      <div class="popup-actions">
                        <button 
                          class="view-all-btn"
                          @click="showAllSensors"
                        >
                          👁️ View All Fastener Data
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Debug Section -->
      <v-row class="mt-4">
        <v-col cols="12">
          <MapboxDebugHelper
            :map="pipelineMap"
            :pipeline-segments="pipelineSegments"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import MapboxPipelineMap from '@/components/MapboxPipelineMap.vue'
import MapboxDebugHelper from '@/components/MapboxDebugHelper.vue'

export default {
  name: 'MapboxTest',
  components: {
    MapboxPipelineMap,
    MapboxDebugHelper
  },
  data() {
    return {
      selectedSegment: null,
      selectedZone: null,
      pipelineMap: null,
      zonePopup: {
        show: false,
        zone: null,
        mouseOver: false
      },
      zonePopupStyle: {},
      hoverTimeout: null
    }
  },
  computed: {
    pipelineSegments() {
      return this.$refs.pipelineMap?.pipelineSegments || []
    }
  },
  mounted() {
    // Wait for map to be ready
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.$refs.pipelineMap && this.$refs.pipelineMap.map) {
          this.pipelineMap = this.$refs.pipelineMap.map
        }
      }, 2000)
    })
  },
  methods: {
    onSegmentSelected(data) {
      console.log('Segment selected:', data)
      this.selectedSegment = data.segment
      this.selectedZone = null // Clear zone selection when segment is selected
    },
    
    onZoneSelected(data) {
      console.log('🎯 Zone selected in test page:', data)
      console.log('Zone details:', {
        id: data.zone.id,
        name: data.zone.name,
        sensorCount: data.zone.sensors.length,
        status: data.zone.status,
        alerts: data.zone.alerts
      })
      this.selectedZone = data.zone
      this.selectedSegment = data.segment
      
      // Scroll to zone details
      this.$nextTick(() => {
        const zoneCard = document.querySelector('.zone-details')
        if (zoneCard) {
          zoneCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },
    
    onZoneHover(data) {
      // Clear any existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
      
      // Add a small delay to prevent popup flickering
      this.hoverTimeout = setTimeout(() => {
        this.zonePopup.show = true
        this.zonePopup.zone = data.zone
        
        // Position popup with offset to avoid covering the zone marker
        let left = data.x + 15  // Use screen coordinates directly
        let top = data.y - 10
        
        // Ensure popup doesn't go off-screen
        const popupWidth = 380
        const popupHeight = 500
        const screenWidth = window.innerWidth
        const screenHeight = window.innerHeight
        
        if (left + popupWidth > screenWidth) {
          left = data.x - popupWidth - 15
        }
        
        if (top + popupHeight > screenHeight) {
          top = data.y - popupHeight + 20
        }
        
        if (top < 0) {
          top = 10
        }
        
        if (left < 0) {
          left = 10
        }
        
        this.zonePopupStyle = {
          left: left + 'px',
          top: top + 'px',
          position: 'fixed'
        }
      }, 300) // 300ms delay
    },
    
    onZoneHoverEnd() {
      // Clear any pending timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
      
      // Add a small delay before hiding to allow moving to the popup
      setTimeout(() => {
        if (!this.zonePopup.mouseOver) {
          this.zonePopup.show = false
        }
      }, 200)
    },
    
    hideZonePopup() {
      this.zonePopup.show = false
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
    },
    
    showAllSensors() {
      // Implement the logic to show all sensors
      console.log('Show all sensors')
    },
    
    getZoneStatusColor(status) {
      const colors = {
        healthy: 'success',
        warning: 'warning',
        critical: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getSensorStatusColor(status) {
      const colors = {
        healthy: 'success',
        warning: 'warning',
        critical: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getSensorTypeColor(type) {
      const colors = {
        'torque': 'primary',        // Blue - torque sensors
        'strain': 'orange',         // Orange - strain gauges
        'temperature': 'red',       // Red - temperature probes
        'movement': 'purple'        // Purple - movement sensors
      }
      return colors[type] || 'grey'
    },
    
    onPopupMouseEnter() {
      this.zonePopup.mouseOver = true
    },
    
    onPopupMouseLeave() {
      this.zonePopup.mouseOver = false
      // Hide popup after a short delay
      setTimeout(() => {
        if (!this.zonePopup.mouseOver) {
          this.zonePopup.show = false
        }
      }, 200)
    }
  }
}
</script>

<style scoped>
.mapbox-test-page {
  padding: 20px;
}

.segment-details,
.zone-details {
  line-height: 1.8;
}

.segment-details > div,
.zone-details > div {
  margin-bottom: 8px;
}

.sensors-card {
  max-height: 500px;
  overflow-y: auto;
}

.sensor-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.sensor-info {
  display: flex;
  flex-direction: column;
}

.sensor-id {
  font-family: monospace;
  font-size: 10px;
  color: #666;
  line-height: 1.2;
}

.sensor-name {
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
}

.sensor-value {
  font-family: monospace;
  font-size: 11px;
  font-weight: 500;
}

::v-deep .v-data-table th {
  font-size: 11px !important;
  padding: 4px 8px !important;
}

::v-deep .v-data-table td {
  padding: 6px 8px !important;
  height: auto !important;
}

::v-deep .sensors-card .v-simple-table {
  max-height: 400px;
  overflow-y: auto;
}

.map-card {
  height: 600px;
}

.map-container-wrapper {
  position: relative;
}

.zone-popup-card {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0;
  min-width: 320px;
  max-width: 380px;
  z-index: 1000;
  animation: fadeInUp 0.2s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.08);
  pointer-events: auto;
  max-height: 500px;
  overflow-y: auto;
}

.zone-popup-card::before {
  content: '';
  position: absolute;
  top: 20px;
  left: -8px;
  border: 8px solid transparent;
  border-right-color: white;
  border-left: none;
}

.zone-popup-card::after {
  content: '';
  position: absolute;
  top: 19px;
  left: -10px;
  border: 9px solid transparent;
  border-right-color: rgba(0, 0, 0, 0.08);
  border-left: none;
  z-index: -1;
}

.zone-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px 16px;
  position: relative;
}

.zone-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: #1976d2;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #666;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  line-height: 1;
}

.close-btn:hover {
  background-color: #f5f5f5;
  color: #333;
}

.zone-popup-content {
  padding: 0 16px 16px 16px;
}

.zone-details-section {
  margin-bottom: 12px;
}

.zone-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 6px 0;
  font-size: 0.875rem;
}

.zone-label {
  color: #424242;
  font-weight: 500;
}

.zone-value {
  color: #1976d2;
  font-weight: 600;
}

.zone-value.alert-count {
  color: #f44336;
}

.zone-divider {
  margin: 12px 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

.sensors-section {
  margin-bottom: 12px;
}

.sensors-header {
  font-size: 1rem;
  font-weight: 500;
  color: #424242;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
}

.sensors-list {
  max-height: 250px;
  overflow-y: auto;
}

.sensor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin: 4px 0;
  background: #f5f5f5;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.sensor-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.sensor-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.sensor-name {
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
  color: #424242;
  margin-bottom: 2px;
}

.sensor-details {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sensor-type-chip {
  font-size: 0.7rem !important;
}

.sensor-value {
  font-family: monospace;
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
}

.sensor-status-icon {
  margin-left: 8px;
}

.more-sensors {
  text-align: center;
  padding: 8px;
  color: #757575;
  font-style: italic;
  font-size: 0.8rem;
}

.popup-actions {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.view-all-btn {
  width: 100%;
  padding: 8px 16px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background-color: #1565c0;
  transform: translateY(-1px);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .zone-popup-card {
    min-width: 280px;
    max-width: 320px;
    max-height: 70vh;
  }
  
  .zone-title {
    font-size: 1.1rem;
  }
  
  .sensor-item {
    padding: 6px 8px;
  }
  
  .sensor-name {
    font-size: 0.75rem;
  }
  
  .sensor-details {
    gap: 4px;
  }
  
  .sensors-list {
    max-height: 200px;
  }
}
</style> 